import { useState, useEffect, useMemo } from "react";
import { getCustomers, authMe, logoutUser } from "../services/api";
import { getHistory } from "../services/history";
import Cars from "./Cars";
import Customers from "./Customers";
import History from "./History";

export default function Dashboard() {
  const [activeTab, setActiveTab] = useState(() => {
    const storedTab = localStorage.getItem("activeTab") || "dashboard";
    return storedTab === "revenue" ? "dashboard" : storedTab;
  });
  const [loading, setLoading] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [history, setHistory] = useState([]);
  const [customerLoading, setCustomerLoading] = useState(false);
  const [detailsCurrentPage, setDetailsCurrentPage] = useState(1);
  const detailsItemsPerPage = 10;

  const getBookingLifecycle = (booking) => {
    if (!booking?.pickupDate || !booking?.dropDate) return "unknown";
    const now = new Date();
    const pickupDate = new Date(booking.pickupDate);
    const dropDate = new Date(booking.dropDate);
    if (now < pickupDate) return "upcoming";
    if (now > dropDate) return "completed";
    return "active";
  };

  const getBookingSortTime = (booking) => {
    const dateValue = booking?.pickupDate || booking?.dropDate;
    const timeValue = dateValue ? new Date(dateValue).getTime() : 0;
    return Number.isFinite(timeValue) ? timeValue : 0;
  };

  useEffect(() => {
    localStorage.setItem("activeTab", activeTab);
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === "customer-details") {
      setCustomerLoading(true);
      Promise.all([getCustomers(), getHistory({})])
        .then(([customersRes, historyRes]) => {
          setCustomers(customersRes.data);
          setHistory(historyRes.data);
        })
        .catch((err) => console.error("Error fetching customer data:", err))
        .finally(() => setCustomerLoading(false));
    }
  }, [activeTab]);

  useEffect(() => {
    getHistory({})
      .then((res) => setHistory(res.data))
      .catch((err) => console.error("Error fetching revenue history:", err));
  }, []);

  useEffect(() => {
    authMe()
      .then(() => setLoading(false))
      .catch(() => (window.location.href = "/"));
  }, []);

  // Close sidebar on outside click / resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) setMenuOpen(false);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleLogout = async () => {
    await logoutUser();
    window.location.href = "/";
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setMenuOpen(false);
  };

  const revenueSummary = useMemo(() => {
    const carMap = new Map();
    let totalRevenue = 0;
    history.forEach((record) => {
      const carName = record.car || "Unknown";
      const revenue = Number(record.finalTotalAmount) || 0;
      const current = carMap.get(carName) || { car: carName, bookings: 0, revenue: 0 };
      current.bookings += 1;
      current.revenue += revenue;
      carMap.set(carName, current);
      totalRevenue += revenue;
    });
    const byCar = Array.from(carMap.values()).sort((a, b) => b.revenue - a.revenue);
    return { totalRevenue, byCar, topCar: byCar[0] || null };
  }, [history]);

  const customerDetailsRows = useMemo(() => {
    const activeRows = customers.map((customer) => ({
      _id: `active-${customer._id}`,
      status: getBookingLifecycle(customer),
      statusLabel:
        getBookingLifecycle(customer) === "upcoming"
          ? "Upcoming"
          : getBookingLifecycle(customer) === "completed"
          ? "Completed"
          : getBookingLifecycle(customer) === "active"
          ? "Active"
          : "Unknown",
      statusBg:
        getBookingLifecycle(customer) === "upcoming"
          ? "#fffbeb"
          : getBookingLifecycle(customer) === "completed"
          ? "#fef2f2"
          : "#dcfce7",
      statusColor:
        getBookingLifecycle(customer) === "upcoming"
          ? "#f59e0b"
          : getBookingLifecycle(customer) === "completed"
          ? "#ef4444"
          : "#166534",
      fullName: customer.fullName,
      phone: customer.phone,
      car: customer.car,
      regNo: customer.regNo,
      pickupDate: customer.pickupDate,
      dropDate: customer.dropDate,
      amount: customer.amount,
    }));

    const completedRows = history.map((record) => ({
      _id: `completed-${record._id}`,
      status: "completed",
      statusLabel: "Completed",
      statusBg: "#fce7f3",
      statusColor: "#be185d",
      fullName: record.customerId?.fullName || "-",
      phone: record.customerId?.phone || "-",
      car: record.car,
      regNo: record.regNo,
      pickupDate: record.pickupDate,
      dropDate: record.dropDate,
      amount: record.finalTotalAmount,
    }));

    const groupedRows = { active: [], upcoming: [], completed: [], unknown: [] };
    [...activeRows, ...completedRows].forEach((row) => {
      const bucket = groupedRows[row.status] ? row.status : "unknown";
      groupedRows[bucket].push(row);
    });

    const sortByDate = (rows) =>
      rows.sort((a, b) => getBookingSortTime(a) - getBookingSortTime(b));

    return [
      ...sortByDate(groupedRows.active),
      ...sortByDate(groupedRows.upcoming),
      ...sortByDate(groupedRows.completed),
      ...sortByDate(groupedRows.unknown),
    ];
  }, [customers, history]);

  const detailsTotalPages = Math.max(
    1,
    Math.ceil(customerDetailsRows.length / detailsItemsPerPage)
  );

  const paginatedCustomerDetailsRows = useMemo(() => {
    const startIndex = (detailsCurrentPage - 1) * detailsItemsPerPage;
    return customerDetailsRows.slice(startIndex, startIndex + detailsItemsPerPage);
  }, [customerDetailsRows, detailsCurrentPage]);

  useEffect(() => {
    setDetailsCurrentPage((page) => Math.min(page, detailsTotalPages));
  }, [detailsTotalPages]);

  const exportToExcel = () => {
    const allData = [
      ...customers.map((c) => ({ fullName: c.fullName, phone: c.phone })),
      ...history.map((h) => ({
        fullName: h.customerId?.fullName || "-",
        phone: h.customerId?.phone || "-",
      })),
    ];
    const uniqueData = Array.from(
      new Map(allData.map((item) => [item.phone, item])).values()
    );
    let csvContent = "Sr. No,Customer Name,Mobile Number\n";
    uniqueData.forEach((item, index) => {
      csvContent += `${index + 1},"${item.fullName}","${item.phone}"\n`;
    });
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `customer_details_${new Date().toISOString().split("T")[0]}.csv`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) return null;

  const tabLabels = {
    dashboard: "Dashboard",
    cars: "Cars",
    customers: "Active Booking",
    "customer-details": "Customer Details",
    history: "History",
  };

  return (
    <>
      <style>{`
        *, *::before, *::after {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
          font-family: Arial, sans-serif;
        }

        /* ── Layout ── */
        .container {
          display: flex;
          min-height: 100vh;
          background: #f5f7fb;
        }

        /* ── Overlay ── */
        .sidebar-overlay {
          display: none;
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.45);
          z-index: 999;
          backdrop-filter: blur(1px);
        }
        .sidebar-overlay.active { display: block; }

        /* ── Sidebar ── */
        .sidebar {
          width: 240px;
          min-width: 240px;
          background: #1e293b;
          color: white;
          padding: 24px 16px;
          display: flex;
          flex-direction: column;
          gap: 6px;
          transition: left 0.28s cubic-bezier(.4,0,.2,1);
          z-index: 1000;
        }

        .sidebar-logo {
          font-size: 20px;
          font-weight: 700;
          padding: 0 10px;
          margin-bottom: 20px;
          color: #fff;
          letter-spacing: 0.5px;
        }

        .sidebar-logo span {
          color: #38bdf8;
        }

        .sidebar nav {
          display: flex;
          flex-direction: column;
          gap: 4px;
          flex: 1;
        }

        .nav-btn {
          display: flex;
          align-items: center;
          gap: 10px;
          width: 100%;
          padding: 11px 14px;
          border: none;
          background: transparent;
          color: #cbd5e1;
          text-align: left;
          cursor: pointer;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 500;
          transition: background 0.18s, color 0.18s;
        }

        .nav-btn:hover {
          background: #334155;
          color: #fff;
        }

        .nav-btn.active {
          background: #0ea5e9;
          color: #fff;
        }

        .nav-btn .nav-icon {
          font-size: 16px;
          width: 20px;
          text-align: center;
          flex-shrink: 0;
        }

        /* ── Main ── */
        .main {
          flex: 1;
          min-width: 0;
          padding: 20px;
          overflow-x: hidden;
        }

        /* ── Topbar ── */
        .topbar {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
          gap: 10px;
        }

        .topbar-left {
          display: flex;
          align-items: center;
          gap: 12px;
          min-width: 0;
        }

        .topbar h2 {
          font-size: 18px;
          font-weight: 700;
          color: #1e293b;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        /* ── Hamburger ── */
        .menu-btn {
          display: none;
          align-items: center;
          justify-content: center;
          width: 40px;
          height: 40px;
          background: #1e293b;
          color: white;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          flex-shrink: 0;
          transition: background 0.18s;
        }
        .menu-btn:hover { background: #334155; }

        .hamburger {
          display: flex;
          flex-direction: column;
          gap: 5px;
          width: 18px;
        }
        .hamburger span {
          display: block;
          height: 2px;
          background: white;
          border-radius: 2px;
          transition: transform 0.25s, opacity 0.25s;
          transform-origin: center;
        }
        .hamburger.open span:nth-child(1) {
          transform: translateY(7px) rotate(45deg);
        }
        .hamburger.open span:nth-child(2) {
          opacity: 0;
          transform: scaleX(0);
        }
        .hamburger.open span:nth-child(3) {
          transform: translateY(-7px) rotate(-45deg);
        }

        /* ── Logout ── */
        .logout {
          background: #ef4444;
          color: white;
          border: none;
          padding: 9px 16px;
          border-radius: 7px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 600;
          flex-shrink: 0;
          transition: background 0.18s;
          white-space: nowrap;
        }
        .logout:hover { background: #dc2626; }

        /* ── Cards ── */
        .cards {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
          gap: 15px;
          margin-bottom: 25px;
        }

        .card {
          background: white;
          padding: 18px;
          border-radius: 12px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.08);
        }

        .card-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 15px;
          flex-wrap: wrap;
          gap: 10px;
        }

        .card-header h3 {
          margin: 0;
          color: #1e293b;
          font-weight: 700;
          font-size: 16px;
        }

        .card-header span {
          font-size: 13px;
          color: #64748b;
        }

        /* ── Table ── */
        .table-container {
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
          border-radius: 8px;
        }

        table {
          width: 100%;
          border-collapse: collapse;
          min-width: 560px;
          background: white;
          border-radius: 10px;
        }

        th, td {
          padding: 12px 14px;
          border-bottom: 1px solid #e2e8f0;
          font-size: 14px;
          white-space: nowrap;
        }

        th {
          background: #f1f5f9;
          color: #475569;
          font-weight: 600;
          text-align: left;
        }

        tbody tr:last-child td { border-bottom: none; }
        tbody tr:hover { background: #f8fafc; }

        /* ── Pagination ── */
        .pagination {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 12px;
          margin-top: 16px;
          flex-wrap: wrap;
        }

        .page-info { color: #64748b; font-size: 13px; }

        .page-controls {
          display: flex;
          gap: 8px;
          align-items: center;
        }

        .page-btn {
          padding: 7px 14px;
          border-radius: 6px;
          border: 1px solid #cbd5e1;
          font-weight: 600;
          font-size: 13px;
          cursor: pointer;
          background: #fff;
          color: #334155;
          transition: background 0.15s, color 0.15s;
        }
        .page-btn:disabled {
          background: #f8fafc;
          color: #94a3b8;
          cursor: not-allowed;
        }
        .page-btn:not(:disabled):hover { background: #f1f5f9; }

        .page-label {
          color: #334155;
          font-weight: 600;
          font-size: 13px;
          padding: 0 4px;
          white-space: nowrap;
        }

        /* ── Export btn ── */
        .export-btn {
          padding: 8px 14px;
          background: #10b981;
          color: white;
          border: none;
          border-radius: 6px;
          font-weight: 600;
          font-size: 13px;
          cursor: pointer;
          white-space: nowrap;
          transition: background 0.18s;
        }
        .export-btn:hover { background: #059669; }

        /* ── Status badge ── */
        .status-badge {
          display: inline-block;
          padding: 3px 10px;
          border-radius: 4px;
          font-size: 12px;
          font-weight: 600;
        }

        /* ── Customer header row ── */
        .customer-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
          flex-wrap: wrap;
          gap: 10px;
        }

        .customer-header-right {
          display: flex;
          gap: 10px;
          align-items: center;
          flex-wrap: wrap;
        }

        /* ── MOBILE ── */
        @media (max-width: 768px) {
          .sidebar {
            position: fixed;
            top: 0;
            left: -260px;
            height: 100%;
            width: 240px;
            padding: 24px 16px 32px;
            box-shadow: 4px 0 24px rgba(0,0,0,0.18);
          }

          .sidebar.open {
            left: 0;
          }

          .menu-btn {
            display: flex;
          }

          .main {
            padding: 14px;
          }

          .topbar {
            margin-bottom: 14px;
          }

          .topbar h2 {
            font-size: 16px;
          }

          .cards {
            grid-template-columns: 1fr 1fr;
            gap: 10px;
          }

          .card {
            padding: 14px;
          }

          .card-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 4px;
          }

          .customer-header {
            flex-direction: column;
            align-items: flex-start;
          }

          .customer-header-right {
            width: 100%;
            justify-content: space-between;
          }

          .pagination {
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
          }

          th, td {
            padding: 10px 10px;
            font-size: 13px;
          }
        }

        @media (max-width: 480px) {
          .cards {
            grid-template-columns: 1fr;
          }

          .topbar h2 {
            font-size: 14px;
          }

          .logout {
            padding: 8px 12px;
            font-size: 13px;
          }
        }
      `}</style>

      {/* Overlay for mobile sidebar */}
      <div
        className={`sidebar-overlay ${menuOpen ? "active" : ""}`}
        onClick={() => setMenuOpen(false)}
      />

      <div className="container">
        {/* Sidebar */}
        <div className={`sidebar ${menuOpen ? "open" : ""}`}>
          <div className="sidebar-logo">Car <span>Admin</span></div>
          <nav>
            {[
              { tab: "dashboard", label: "Dashboard" },
              { tab: "cars", label: "Cars" },
              { tab: "customers", label: "Active Booking" },
              { tab: "customer-details", label: "Customer Details" },
              { tab: "history", label: "History" },
            ].map(({ tab, icon, label }) => (
              <button
                key={tab}
                className={`nav-btn ${activeTab === tab ? "active" : ""}`}
                onClick={() => handleTabChange(tab)}
              >
                <span className="nav-icon">{icon}</span>
                {label}
              </button>
            ))}
          </nav>
        </div>

        {/* Main */}
        <div className="main">
          {/* Topbar */}
          <div className="topbar">
            <div className="topbar-left">
              <button className="menu-btn" onClick={() => setMenuOpen(!menuOpen)}>
                <div className={`hamburger ${menuOpen ? "open" : ""}`}>
                  <span />
                  <span />
                  <span />
                </div>
              </button>
              <h2>{tabLabels[activeTab] || activeTab.toUpperCase()}</h2>
            </div>
            <button className="logout" onClick={handleLogout}>
              Logout
            </button>
          </div>

          {/* Dashboard Tab */}
          {activeTab === "dashboard" && (
            <div className="card" style={{ marginBottom: 25 }}>
              <div className="card-header" style={{ marginBottom: 18 }}>
                <h3>Car-wise Revenue Analytics</h3>
                <span>
                  {revenueSummary.byCar.length} cars • Top car:{" "}
                  {revenueSummary.topCar ? revenueSummary.topCar.car : "NA"}
                </span>
              </div>

              <div className="cards" style={{ marginBottom: 18 }}>
                <div
                  className="card"
                  style={{
                    background: "linear-gradient(135deg, #0ea5e9, #2563eb)",
                    color: "#fff",
                  }}
                >
                  <h3 style={{ color: "#fff", marginBottom: 8 }}>Total Revenue</h3>
                  <p style={{ fontSize: 26, fontWeight: 700 }}>
                    ₹{revenueSummary.totalRevenue.toLocaleString("en-IN")}
                  </p>
                </div>
                <div className="card" style={{ background: "#f8fafc" }}>
                  <h3 style={{ marginBottom: 8 }}>Top Earning Car</h3>
                  <p style={{ fontSize: 20, fontWeight: 700, color: "#0f172a" }}>
                    {revenueSummary.topCar ? revenueSummary.topCar.car : "NA"}
                  </p>
                  <span style={{ color: "#64748b", fontSize: 14 }}>
                    ₹
                    {revenueSummary.topCar
                      ? revenueSummary.topCar.revenue.toLocaleString("en-IN")
                      : 0}
                  </span>
                </div>
                <div className="card" style={{ background: "#f8fafc" }}>
                  <h3 style={{ marginBottom: 8 }}>Completed Trips</h3>
                  <p style={{ fontSize: 26, fontWeight: 700, color: "#0f172a" }}>
                    {history.length}
                  </p>
                </div>
              </div>

              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th style={{ width: "42%" }}>Car</th>
                      <th style={{ width: "18%" }}>Bookings</th>
                      <th style={{ width: "25%" }}>Revenue</th>
                      <th style={{ width: "15%" }}>Share</th>
                    </tr>
                  </thead>
                  <tbody>
                    {revenueSummary.byCar.length === 0 ? (
                      <tr>
                        <td
                          colSpan="4"
                          style={{ textAlign: "center", color: "#64748b" }}
                        >
                          No completed bookings found yet.
                        </td>
                      </tr>
                    ) : (
                      revenueSummary.byCar.map((item) => {
                        const share =
                          revenueSummary.totalRevenue > 0
                            ? (
                                (item.revenue / revenueSummary.totalRevenue) *
                                100
                              ).toFixed(1)
                            : "0.0";
                        return (
                          <tr key={item.car}>
                            <td>
                              <strong>{item.car}</strong>
                              <div
                                style={{
                                  marginTop: 6,
                                  height: 6,
                                  borderRadius: 999,
                                  background: "#e2e8f0",
                                  overflow: "hidden",
                                }}
                              >
                                <div
                                  style={{
                                    width: `${share}%`,
                                    height: "100%",
                                    borderRadius: 999,
                                    background:
                                      "linear-gradient(90deg, #0ea5e9, #2563eb)",
                                  }}
                                />
                              </div>
                            </td>
                            <td>{item.bookings}</td>
                            <td>₹{item.revenue.toLocaleString("en-IN")}</td>
                            <td>{share}%</td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === "cars" && <Cars />}
          {activeTab === "customers" && <Customers />}
          {activeTab === "history" && <History />}

          {/* Customer Details Tab */}
          {activeTab === "customer-details" && (
            <div className="card">
              <div className="customer-header">
                <h3 style={{ color: "#1e293b", fontWeight: 700, fontSize: 16 }}>
                  Customer Details
                </h3>
                <div className="customer-header-right">
                  <span style={{ fontSize: 13, color: "#64748b" }}>
                    {customers.length} Active • {history.length} Completed
                  </span>
                  <button className="export-btn" onClick={exportToExcel}>
                    Export to Excel
                  </button>
                </div>
              </div>

              {customerLoading ? (
                <p style={{ color: "#64748b" }}>Loading...</p>
              ) : customerDetailsRows.length === 0 ? (
                <p style={{ color: "#64748b" }}>No customers found</p>
              ) : (
                <div className="table-container">
                  <table>
                    <thead>
                      <tr>
                        <th>Status</th>
                        <th>Customer Name</th>
                        <th>Mobile Number</th>
                        <th>Car Model</th>
                        <th>Reg. No</th>
                        <th>Pickup Date</th>
                        <th>Drop Date</th>
                        <th>Final Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedCustomerDetailsRows.map((row) => (
                        <tr
                          key={row._id}
                          style={{ opacity: row.status === "completed" ? 0.8 : 1 }}
                        >
                          <td>
                            <span
                              className="status-badge"
                              style={{
                                background: row.statusBg,
                                color: row.statusColor,
                              }}
                            >
                              {row.statusLabel || row.status}
                            </span>
                          </td>
                          <td>
                            <strong>{row.fullName}</strong>
                          </td>
                          <td>{row.phone}</td>
                          <td>{row.car}</td>
                          <td>{row.regNo}</td>
                          <td>
                            {row.pickupDate
                              ? new Date(row.pickupDate).toLocaleDateString("en-IN")
                              : "-"}
                          </td>
                          <td>
                            {row.dropDate
                              ? new Date(row.dropDate).toLocaleDateString("en-IN")
                              : "-"}
                          </td>
                          <td>₹{row.amount}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {!customerLoading && customerDetailsRows.length > 0 && (
                <div className="pagination">
                  <div className="page-info">
                    Showing{" "}
                    {Math.min(
                      (detailsCurrentPage - 1) * detailsItemsPerPage + 1,
                      customerDetailsRows.length
                    )}
                    –
                    {Math.min(
                      detailsCurrentPage * detailsItemsPerPage,
                      customerDetailsRows.length
                    )}{" "}
                    of {customerDetailsRows.length}
                  </div>
                  <div className="page-controls">
                    <button
                      type="button"
                      className="page-btn"
                      onClick={() =>
                        setDetailsCurrentPage((p) => Math.max(1, p - 1))
                      }
                      disabled={detailsCurrentPage === 1}
                    >
                      Prev
                    </button>
                    <span className="page-label">
                      Page {detailsCurrentPage} of {detailsTotalPages}
                    </span>
                    <button
                      type="button"
                      className="page-btn"
                      onClick={() =>
                        setDetailsCurrentPage((p) =>
                          Math.min(detailsTotalPages, p + 1)
                        )
                      }
                      disabled={detailsCurrentPage === detailsTotalPages}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}